import {
  Button,
  buttonVariants,
  Input,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { cn } from '@folio/lib';
import { type InfiniteData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  emailAccountControllerListMineQueryOptions,
  emailThreadControllerGetThreadQueryOptions,
  emailThreadControllerListThreadsQueryKey,
  emailThreadControllerSetThreadsArchived,
  generalInboxControllerGetSummaryQueryKey,
  type EmailThreadDto,
  type ListEmailThreadsOutput,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import { Archive, ArchiveRestore, Search, SquareArrowOutUpRight, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ThreadReadingPane } from '../GeneralInboxModule/ThreadReadingPane';
import { ToggleGroup } from '../GeneralInboxModule/ToggleGroup';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import {
  emailsToAttach,
  hasAnyAttachment,
  invalidateEmailQueries,
  usePolicyEmailThreads,
} from './policyEmailThreads';
import { PolicyThreadRow } from './PolicyThreadRow';
import { ToAttachBanner } from './ToAttachBanner';

type Filter = 'all' | 'to_attach' | 'unread' | 'attachments';

const FILTERS: Filter[] = ['all', 'to_attach', 'unread', 'attachments'];

const matchesFilter = (filter: Filter, thread: EmailThreadDto, policyId: string) => {
  if (filter === 'to_attach') return emailsToAttach(thread, policyId).length > 0;
  if (filter === 'unread') return thread.isUnread;
  if (filter === 'attachments') return hasAnyAttachment(thread);
  return true;
};

export function PolicyExchangesModule({ policyId }: { policyId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedThreadId = searchParams.get('thread');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const searched = useDebouncedValue(search.trim(), 300);
  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    refetch,
  } = usePolicyEmailThreads({ policyId, search: searched });
  // Banner and tab badge ignore the search and the filters
  const { data: allData } = usePolicyEmailThreads({ policyId });
  const allThreads = allData?.pages.flatMap(({ threads }) => threads) ?? [];
  const searchedThreads = data?.pages.flatMap(({ threads }) => threads) ?? [];
  const threads = searchedThreads.filter(thread => matchesFilter(filter, thread, policyId));
  // Filters run on loaded pages: keep loading until one matches or none are left
  useEffect(() => {
    if (threads.length === 0 && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      fetchNextPage();
    }
  }, [threads.length, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  // Read from the reading pane's fetch: a deep-linked thread may sit beyond the loaded pages
  const { data: openedDetail } = useQuery({
    ...emailThreadControllerGetThreadQueryOptions(openedThreadId ?? ''),
    enabled: false,
  });
  const openedThread =
    searchedThreads.find(({ id }) => id === openedThreadId) ??
    (openedDetail?.id === openedThreadId
      ? {
          account: openedDetail.account,
          isArchived: openedDetail.emails.every(({ isArchived }) => isArchived),
        }
      : undefined);
  const isFiltered = filter !== 'all' || searched !== '';
  const { data: mailboxes } = useQuery(emailAccountControllerListMineQueryOptions());
  const { mutate: setArchived, isPending: isArchiving } = useMutation({
    mutationFn: ({
      threadId,
      ...body
    }: {
      threadId: string;
      archived: boolean;
      emailIds?: string[];
    }) => emailThreadControllerSetThreadsArchived({ threadIds: [threadId], ...body }),
    onSettled: (_, __, { threadId }) => invalidateEmailQueries(queryClient, threadId),
  });

  const selectThread = (threadId: string | null) =>
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (threadId) next.set('thread', threadId);
      else next.delete('thread');
      next.delete('email');
      return next;
    });

  const markThreadRead = useCallback(
    (threadId: string) => {
      let flipped = false;
      queryClient.setQueriesData<InfiniteData<ListEmailThreadsOutput>>(
        { queryKey: emailThreadControllerListThreadsQueryKey() },
        previous =>
          previous && {
            ...previous,
            pages: previous.pages.map(page => ({
              ...page,
              threads: page.threads.map(thread => {
                if (thread.id !== threadId || !thread.isUnread) return thread;
                flipped = true;
                return { ...thread, isUnread: false };
              }),
            })),
          },
      );
      if (flipped)
        queryClient.invalidateQueries({ queryKey: generalInboxControllerGetSummaryQueryKey() });
    },
    [queryClient],
  );

  const canArchive =
    openedThread !== undefined &&
    (!openedThread.account.isShared || (mailboxes?.canManageSharedAccounts ?? false));

  const toolbar = (
    <Group wrap gap={2}>
      <div className="relative w-full max-w-sm">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={t('policyExchanges.searchPlaceholder')}
          aria-label={t('policyExchanges.searchPlaceholder')}
          className="pl-9"
          data-testid="policy-exchanges-search"
        />
      </div>
      <ToggleGroup
        label={t('policyExchanges.filters.label')}
        options={FILTERS}
        value={filter}
        onChange={setFilter}
        optionLabel={option => t(`policyExchanges.filters.${option}`)}
      />
    </Group>
  );

  const list =
    threads.length === 0 && !hasNextPage ? (
      <Stack align="center" gap={2} className="px-4 py-10 text-center">
        <Text size="sm" tone="muted">
          {t('policyExchanges.emptyFiltered')}
        </Text>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setFilter('all');
            setSearch('');
          }}
        >
          {t('policyExchanges.resetFilters')}
        </Button>
      </Stack>
    ) : (
      <Stack gap={0} className={cn(openedThreadId && 'min-h-0 overflow-y-auto border-r')}>
        <ul>
          {threads.map(thread => (
            <PolicyThreadRow
              key={thread.id}
              thread={thread}
              policyId={policyId}
              isSelected={thread.id === openedThreadId}
              onOpen={() => selectThread(thread.id)}
            />
          ))}
        </ul>
        {isFetchNextPageError && (
          <Text size="sm" tone="destructive" role="alert" className="p-3">
            {t('generalInbox.loadMoreError')}
          </Text>
        )}
        {hasNextPage && (
          <Button
            variant="ghost"
            className="m-2"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {t(
              isFetchingNextPage
                ? 'generalInbox.loadingMore'
                : isFetchNextPageError
                  ? 'generalInbox.retry'
                  : 'policyExchanges.loadMore',
            )}
          </Button>
        )}
      </Stack>
    );

  const content =
    isError && !data ? (
      <Stack gap={2} role="alert" className="text-sm text-destructive">
        <p>{t('generalInbox.error')}</p>
        <Button variant="outline" onClick={() => refetch()}>
          {t('generalInbox.retry')}
        </Button>
      </Stack>
    ) : isPending ? (
      <Skeleton className="h-32 w-full" />
    ) : openedThreadId ? (
      <div className="grid h-[calc(100vh-18rem)] min-h-96 grid-cols-[minmax(18rem,24rem)_1fr] overflow-hidden rounded-lg border bg-card">
        {list}
        <ThreadReadingPane
          key={openedThreadId}
          threadId={openedThreadId}
          emailId={searchParams.get('email')}
          emailSelectionCount={0}
          policyId={policyId}
          onRead={markThreadRead}
          onSetArchived={(emailId, archived) =>
            setArchived({ threadId: openedThreadId, archived, emailIds: [emailId] })
          }
          actions={
            <div className="flex shrink-0 items-center gap-1">
              {canArchive && (
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={
                    openedThread.isArchived ? (
                      <ArchiveRestore className="size-4" />
                    ) : (
                      <Archive className="size-4" />
                    )
                  }
                  disabled={isArchiving}
                  onClick={() =>
                    setArchived({ threadId: openedThreadId, archived: !openedThread.isArchived })
                  }
                  data-testid="policy-exchanges-archive-thread"
                >
                  {t(
                    openedThread.isArchived
                      ? 'policyExchanges.unarchive'
                      : 'policyExchanges.archive',
                  )}
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/general-inbox?thread=${encodeURIComponent(openedThreadId)}`}
                    className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                    aria-label={t('policyExchanges.openInInbox')}
                  >
                    <SquareArrowOutUpRight className="size-4" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>{t('policyExchanges.openInInbox')}</TooltipContent>
              </Tooltip>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('policyExchanges.close')}
                onClick={() => selectThread(null)}
              >
                <X className="size-4" />
              </Button>
            </div>
          }
        />
      </div>
    ) : (
      <div className="overflow-hidden rounded-lg border bg-card">{list}</div>
    );

  return (
    <Stack gap={3} asChild>
      <section aria-label={t('policyDashboardPages.exchanges')}>
        {allData && allThreads.length === 0 && !isFiltered ? (
          <Text size="sm" tone="muted" className="py-10 text-center">
            {t('policyExchanges.empty')}
          </Text>
        ) : (
          <>
            <ToAttachBanner threads={allThreads} policyId={policyId} />
            {toolbar}
            {content}
          </>
        )}
      </section>
    </Stack>
  );
}
